const DB_NAME = "UserDeviceDB";
const STORE_NAME = "UserDevices";
const DB_VERSION = 1;

interface UserDevice {
  username: string;
  deviceToken: string;
}

function isIndexedDBSupported(): boolean {
  return typeof indexedDB !== "undefined";
}

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: "username" });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function storeUserDevice(username: string, deviceToken: string): Promise<void> {
  const data: UserDevice = { username, deviceToken };

  if (isIndexedDBSupported()) {
    try {
      const db = await openDB();
      const tx = db.transaction(STORE_NAME, "readwrite");
      const store = tx.objectStore(STORE_NAME);
      store.put(data);
      await new Promise<void>((resolve, reject) => {
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
        tx.onabort = () => reject(tx.error);
      });
      db.close();
      return;
    } catch (err) {
      console.warn("IndexedDB failed, falling back to sessionStorage", err);
    }
  }

  // Fallback to sessionStorage
  sessionStorage.setItem(`user-device-${username}`, JSON.stringify(data));
}

export async function getDeviceToken(username: string): Promise<string | null> {
  if (isIndexedDBSupported()) {
    try {
      const db = await openDB();
      const tx = db.transaction(STORE_NAME, "readonly");
      const store = tx.objectStore(STORE_NAME);
      const request = store.get(username);

      return new Promise((resolve, reject) => {
        request.onsuccess = () => {
          const result = request.result as UserDevice | undefined;
          resolve(result?.deviceToken || null);
        };
        request.onerror = () => reject(request.error);
      });
    } catch (err) {
      console.warn("IndexedDB get failed, falling back to sessionStorage", err);
    }
  }

  // Fallback to sessionStorage
  const raw = sessionStorage.getItem(`user-device-${username}`);
  if (!raw) return null;
  try {
    const data: UserDevice = JSON.parse(raw);
    return data.deviceToken || null;
  } catch {
    return null;
  }
}
